/**
 * SSOT לתמחור פלואו טיסות — פונקציות טהורות בלבד.
 *
 * מקור אמת *יחיד*: BookingFlowContext.pricing. הפונקציות כאן הן helpers לחישוב
 * (לא hooks, לא context). כל לוגיקת base + handling fee + credit card fee
 * מרוכזת כאן, כדי שמי שמרכיב את ה-state (BookingFlowContext actions) יקבל
 * אובייקט pricing שלם ואחיד — ושהמסכים יצרכו אותו כתוצאה גמורה בלבד.
 */
import { applyLdsHandlingFeeAndCardToFareDetails } from "../../../../pricing/vacationPackageLdsAdapter";
import { computeSupplementSelectionExtrasTotal } from "../../adapters/flightOfferAdapter";

/** שמר את שמירת הגודל בפלואו — אחרי כל שינוי selection מחשבים מחדש */
export const EMPTY_PRICING = Object.freeze({
  base: 0,
  flight: 0,
  hotel: 0,
  extras: 0,
  handlingFee: 0,
  creditCardFee: 0,
  total: 0,
  currency: "",
  source: "empty",
  managedOurTotalFare: null,
});

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function computeExtrasTotalIls(extras) {
  const ex = extras || {};
  const supplementTotal = computeSupplementSelectionExtrasTotal(
    ex.supplementPaxSelections,
    ex.supplementUnitFaresIls && Object.keys(ex.supplementUnitFaresIls).length
      ? ex.supplementUnitFaresIls
      : ex.supplementUnitFares
  );
  const mockBaggage = Array.isArray(ex.baggage)
    ? ex.baggage.reduce((acc, b) => acc + safeNum(b?.priceUsd), 0)
    : 0;
  return safeNum(supplementTotal) + mockBaggage;
}

/**
 * מחשב pricing מ-LDS rawDeal: base→handlingFee→creditCardFee→total.
 * @returns {null | { base:number, handlingFee:number, creditCardFee:number, total:number, currency:string, managedOurTotalFare:object|null, source:string }}
 */
export function computeLdsFlightPricing({
  rawDeal,
  calculateLDSDealFareForRoomsComposition,
  handleFeeData,
  roomsAndPaxInfo,
  getCreditCardFeePercent,
  fixedFlightCommission,
}) {
  if (!rawDeal || typeof calculateLDSDealFareForRoomsComposition !== "function") {
    return null;
  }
  const baseFare = calculateLDSDealFareForRoomsComposition(rawDeal);
  if (!baseFare?.ourTotalFare) return null;

  const { ourFareDetails } = applyLdsHandlingFeeAndCardToFareDetails({
    ourFareDetails: baseFare,
    numberOfRooms: Math.max(
      1,
      Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo.length : 1
    ),
    handleFeeData,
    getCreditCardFeePercent,
    fixedFlightCommission,
  });

  const managed = ourFareDetails?.ourTotalFare || baseFare?.ourTotalFare || null;
  const base = safeNum(managed?.priceWithCommission);
  const priceWithHandleFee = safeNum(managed?.priceWithHandleFee);
  const handlingFee = safeNum(managed?.handleFee);
  const creditCardFee = safeNum(managed?.creditCardFee);
  const totalWithAll = safeNum(managed?.priceWithCommissionAndCreditCardFee);

  const total =
    totalWithAll > 0
      ? totalWithAll
      : priceWithHandleFee > 0
        ? priceWithHandleFee + creditCardFee
        : base + handlingFee + creditCardFee;

  // אם המחיר הסופי 0 או שאין base, נחזיר null כדי לאפשר fallback
  if (total <= 0 && base <= 0) {
    return null;
  }

  return {
    base,
    handlingFee,
    creditCardFee,
    total,
    currency: "ILS",
    managedOurTotalFare: managed,
    source: "lds",
  };
}

/**
 * מסלול fallback כש-LDS אינו זמין (self-compose וכד'): משתמש במחירים שחושבו
 * upstream (offer.priceClientTotalIls / ssot.totalBooking) בלי עמלות נוספות.
 */
export function computeFallbackFlightPricing({
  selectedFlightOffer,
  selfComposeSsot,
  extras,
}) {
  const extrasIls = computeExtrasTotalIls(extras);
  if (
    selfComposeSsot &&
    Number.isFinite(Number(selfComposeSsot.totalBooking)) &&
    Number(selfComposeSsot.totalBooking) > 0
  ) {
    const flight = Number(selfComposeSsot.totalBooking);
    return {
      base: flight,
      flight,
      hotel: 0,
      extras: 0, // כבר מגולם ב-totalBooking דרך baggageAdd
      handlingFee: 0,
      creditCardFee: 0,
      total: flight,
      currency: selfComposeSsot.currency || "",
      managedOurTotalFare: null,
      source: "selfCompose",
    };
  }
  const flightIls = safeNum(selectedFlightOffer?.priceClientTotalIls);
  const flightUsd = safeNum(selectedFlightOffer?.priceUsd);
  
  // fallback נוספים: totalFare, rawTotalFare, או חישוב מ-pricePerPerson
  const totalFare = safeNum(selectedFlightOffer?.totalFare);
  const rawTotalFare = safeNum(selectedFlightOffer?.rawTotalFare);
  const pricePerPerson = safeNum(selectedFlightOffer?.pricePerPerson);
  const totalPaxCount = safeNum(selectedFlightOffer?.totalPaxCount);
  const calculatedFromPerPerson = 
    pricePerPerson > 0 && totalPaxCount > 0 
      ? pricePerPerson * totalPaxCount 
      : 0;
  
  // בחירת המחיר: ILS > USD > totalFare > rawTotalFare > calculated > 0
  const flight = 
    flightIls > 0 ? flightIls :
    flightUsd > 0 ? flightUsd :
    totalFare > 0 ? totalFare :
    rawTotalFare > 0 ? rawTotalFare :
    calculatedFromPerPerson > 0 ? calculatedFromPerPerson :
    0;
  
  // זיהוי מטבע: ILS אם flightIls קיים, אחרת לפי המקור או ברירת מחדל USD
  const rawCurrency = String(selectedFlightOffer?.rawCurrency || "").trim().toUpperCase();
  const currency = 
    flightIls > 0 ? "ILS" : 
    rawCurrency && rawCurrency !== "" ? rawCurrency :
    "USD";
  
  return {
    base: flight,
    flight,
    hotel: 0,
    extras: extrasIls,
    handlingFee: 0,
    creditCardFee: 0,
    total: flight + extrasIls,
    currency,
    managedOurTotalFare: null,
    source: "offer",
  };
}

/**
 * מחשב את ה-pricing המלא (SSOT) על פי הקלט. חוקים:
 *  - אם יש rawDeal + calculateLDS* → עוברים דרך ה-LDS pipeline (כולל handlingFee/creditCardFee).
 *    extras (ILS) מתווספים מעל base ה-LDS אם קיימים.
 *  - אחרת → fallback ל-self-compose SSOT → אחרת מחיר offer פשוט.
 */
export function computeFlightBookingPricing({
  selectedFlightOffer,
  selfComposeSsot,
  extras,
  calculateLDSDealFareForRoomsComposition,
  handleFeeData,
  roomsAndPaxInfo,
  getCreditCardFeePercent,
  fixedFlightCommission,
}) {
  const rawDeal = selectedFlightOffer?.rawDeal ?? null;
  const extrasIls = computeExtrasTotalIls(extras);

  const lds = computeLdsFlightPricing({
    rawDeal,
    calculateLDSDealFareForRoomsComposition,
    handleFeeData,
    roomsAndPaxInfo,
    getCreditCardFeePercent,
    fixedFlightCommission,
  });

  if (lds) {
    return {
      base: lds.base,
      flight: lds.base,
      hotel: 0,
      extras: extrasIls,
      handlingFee: lds.handlingFee,
      creditCardFee: lds.creditCardFee,
      total: lds.total + extrasIls,
      currency: lds.currency,
      managedOurTotalFare: lds.managedOurTotalFare,
      source: "lds",
    };
  }

  return computeFallbackFlightPricing({
    selectedFlightOffer,
    selfComposeSsot,
    extras,
  });
}

/**
 * השוואה שטוחה של אובייקט pricing — למניעת setState מיותר ב-sync.
 */
export function flightPricingEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.base === b.base &&
    a.flight === b.flight &&
    a.hotel === b.hotel &&
    a.extras === b.extras &&
    a.handlingFee === b.handlingFee &&
    a.creditCardFee === b.creditCardFee &&
    a.total === b.total &&
    a.currency === b.currency &&
    a.source === b.source &&
    a.managedOurTotalFare === b.managedOurTotalFare
  );
}
